// What the coach page reads back from the stored conversations: the one to pick up, and whether a send got through.

import type { CoachConversation, CoachMessage } from '../../../../../../pure-js/types/CoachTypes'
import { COACH_RESUME_GRACE_MINUTES } from '../../../../../../pure-js/libs/AICoachStateHelper'
import { ONE_MINUTE_MS } from '../../../../../../pure-js/libs/Consts'

/**
 * The most recently active conversation that has not been concluded — and only while the server would
 * still resume it. Without the window the page offered to continue a thread from days ago, the server
 * refused and opened a new one instead, and every visit left another conversation behind.
 */
export function findMostRecentOpenConversation(
  conversations: CoachConversation[] | undefined
): CoachConversation | undefined {
  return (conversations || [])
    .filter((conversation) => !conversation.endedAt)
    .sort((first, second) =>
      (second.lastMessageAt || second.startedAt).localeCompare(first.lastMessageAt || first.startedAt)
    )[0]
}

export function findResumableConversationId(
  conversations: CoachConversation[] | undefined,
  now = Date.now()
): string | undefined {
  const mostRecentOpen = findMostRecentOpenConversation(conversations)
  if (!mostRecentOpen) return undefined

  const lastActivityAt = new Date(mostRecentOpen.lastMessageAt || mostRecentOpen.startedAt).getTime()
  const minutesSinceActivity = (now - lastActivityAt) / ONE_MINUTE_MS
  return minutesSinceActivity > COACH_RESUME_GRACE_MINUTES ? undefined : mostRecentOpen.id
}

/** A typed message whose answer never came back: the id the page gave it, and when it was sent. */
export type SentCoachMessage = {
  conversationId: string
  clientMessageId: string
  sentAt: number
}

/** Whether the stored thread holds the message the page sent, and the reply she wrote to it if she has. */
export function findTurnOfSentMessage(
  messages: CoachMessage[],
  clientMessageId: string
): { isStored: boolean; reply?: CoachMessage } {
  const stored = messages.find((message) => message.clientMessageId === clientMessageId)
  if (!stored) return { isStored: false }

  return { isStored: true, reply: messages.find((message) => message.replyToMessageId === stored.id) }
}
